import { useState } from 'react';
import { supabase } from './supabaseClient';
import { showToast } from './Toast';
import { NEUTRAL, STATUS_COLORS, resolveTheme } from './designTokens';
import Card from './components/primitives/Card';
import Button from './components/primitives/Button';
import Badge from './components/primitives/Badge';
import EmptyState from './components/primitives/EmptyState';
import ListItem from './components/primitives/ListItem';
import FormInput from './components/primitives/FormInput';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';

// ─── Redigerbart block-header med FAS-badge ───────────────────────────────────
function EditableBlock({ block, blockIndex, theme, onSave, onDelete, editing, setEditing }) {
  const [title, setTitle] = useState(block.title);
  const [startWeek, setStartWeek] = useState(block.start_week);
  const [endWeek, setEndWeek] = useState(block.end_week);

  const save = async () => {
    const { error } = await supabase
      .from('program_blocks')
      .update({ title, start_week: Number(startWeek), end_week: Number(endWeek) })
      .eq('id', block.id);
    if (error) showToast('Kunde inte uppdatera block: ' + error.message, 'error');
    else {
      setEditing(false);
      onSave({ ...block, title, start_week: Number(startWeek), end_week: Number(endWeek) });
    }
  };

  const fasLabel = `FAS ${(blockIndex >= 0 ? blockIndex : 0) + 1}`;

  if (editing) {
    return (
      <div style={{
        border: `1px solid ${theme.accent}`,
        boxShadow: `0 0 0 3px ${theme.accent}18`,
        borderRadius: '12px', overflow: 'hidden', marginBottom: '16px',
      }}>
        <div style={{ background: theme.accent_soft_bg, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10px', fontWeight: '600', background: theme.accent, color: 'white', padding: '2px 8px', borderRadius: '999px', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
            {fasLabel}
          </span>
          <input
            autoFocus
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Namnge fasen (t.ex. Mobilitet)"
            style={{ flex: 1, minWidth: '120px', background: 'transparent', border: 'none', outline: 'none', fontSize: '13px', fontWeight: '500', color: theme.text_primary }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <span style={{ fontSize: '11px', color: theme.text_secondary }}>Vecka</span>
            <input type="number" min="1" value={startWeek} onChange={e => setStartWeek(e.target.value)}
              style={{ width: '40px', padding: '3px 5px', border: `1px solid ${theme.card_border}`, borderRadius: '5px', fontSize: '12px', textAlign: 'center', outline: 'none' }} />
            <span style={{ fontSize: '11px', color: theme.text_secondary }}>–</span>
            <input type="number" min="1" value={endWeek} onChange={e => setEndWeek(e.target.value)}
              style={{ width: '40px', padding: '3px 5px', border: `1px solid ${theme.card_border}`, borderRadius: '5px', fontSize: '12px', textAlign: 'center', outline: 'none' }} />
            <Button theme={theme} variant="primary" size="sm" onClick={save}>Spara</Button>
            <button onClick={() => setEditing(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: theme.text_secondary, padding: '0 4px' }}>
              Avbryt
            </button>
            <button onClick={onDelete}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: STATUS_COLORS.error, fontSize: '11px', fontWeight: '500', padding: '0 4px' }}>
              Ta bort
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'white', border: `1px solid ${theme.card_border}`,
      borderRadius: '12px', overflow: 'hidden', marginBottom: '16px',
    }}>
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ color: theme.card_border, fontSize: '14px', cursor: 'default', marginRight: '2px', letterSpacing: '-2px' }}>⋮⋮</span>
        <span style={{ fontSize: '10px', fontWeight: '600', background: theme.accent, color: 'white', padding: '2px 8px', borderRadius: '999px', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
          {fasLabel}
        </span>
        <span style={{ fontSize: '13px', fontWeight: '500', color: theme.text_primary, flex: 1 }}>{block.title}</span>
        <span style={{ fontSize: '11px', color: theme.text_secondary }}>v.{block.start_week}–{block.end_week}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
          <button onClick={() => setEditing(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: theme.text_secondary, padding: '0 2px' }}
            onMouseEnter={e => e.currentTarget.style.color = theme.accent}
            onMouseLeave={e => e.currentTarget.style.color = theme.text_secondary}
          >Redigera</button>
          <span style={{ color: theme.card_border }}>|</span>
          <button onClick={onDelete}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: STATUS_COLORS.error, fontWeight: '500', padding: '0 2px' }}>
            Ta bort
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Doseringsformulär (inline) ───────────────────────────────────────────────
function DoseForm({ initial, onSave, onCancel, saveLabel, theme }) {
  const [sets, setSets] = useState(initial?.sets ? String(initial.sets) : '');
  const [reps, setReps] = useState(initial?.reps ? String(initial.reps) : '');
  const [duration, setDuration] = useState(initial?.duration_seconds ? String(initial.duration_seconds) : '');
  const [rest, setRest] = useState(initial?.rest_seconds ? String(initial.rest_seconds) : '');
  const [notes, setNotes] = useState(initial?.notes || '');

  const build = () => ({
    sets: sets ? Number(sets) : null,
    reps: reps ? Number(reps) : null,
    duration_seconds: duration ? Number(duration) : null,
    rest_seconds: rest ? Number(rest) : null,
    notes: notes.trim() || null,
  });

  return (
    <Card theme={theme} style={{ padding: '12px 14px', marginTop: '8px' }}>
      <p style={{ margin: '0 0 8px', fontSize: '0.8em', fontWeight: '600', color: NEUTRAL.text }}>Set & reps (valfritt)</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
        <FormInput theme={theme} label="Set" type="number" min="1" value={sets} onChange={e => setSets(e.target.value)} placeholder="–" style={{ marginBottom: 0 }} />
        <FormInput theme={theme} label="Rep" type="number" min="1" value={reps} onChange={e => setReps(e.target.value)} placeholder="–" style={{ marginBottom: 0 }} />
        <FormInput theme={theme} label="Tid (s)" type="number" min="1" value={duration} onChange={e => setDuration(e.target.value)} placeholder="–" style={{ marginBottom: 0 }} />
        <FormInput theme={theme} label="Vila (s)" type="number" min="1" value={rest} onChange={e => setRest(e.target.value)} placeholder="–" style={{ marginBottom: 0 }} />
      </div>
      <FormInput
        theme={theme}
        label="Instruktion / anteckning"
        type="text"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="T.ex. Håll spänningen i 2s, öka gradvis..."
      />
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button theme={theme} variant="primary" onClick={() => onSave(build())}>{saveLabel || 'Spara'}</Button>
        <Button theme={theme} variant="secondary" onClick={onCancel}>Avbryt</Button>
      </div>
    </Card>
  );
}

// ─── Dostext-hjälp ────────────────────────────────────────────────────────────
function doseText(pe) {
  const parts = [];
  if (pe.sets) parts.push(`${pe.sets} set`);
  if (pe.reps) parts.push(`${pe.reps} reps`);
  if (pe.duration_seconds) parts.push(`${pe.duration_seconds}s`);
  if (pe.rest_seconds) parts.push(`${pe.rest_seconds}s vila`);
  return parts.join(' · ');
}

// ─── Blockdetaljvy ────────────────────────────────────────────────────────────
function ProgramBlockDetail({
  selectedBlock,
  selectedProgram,
  blockIndex,
  exercises,
  blockExercises,
  pendingExercise,
  editingPeId,
  exSearch,
  exCategory,
  accentColor = KLINIO_DEFAULT_ACCENT,
  onBack,
  onSaveBlock,
  onSetPendingExercise,
  onSetEditingPeId,
  onSetExSearch,
  onSetExCategory,
  onAddExercise,
  onUpdateExerciseDose,
  onRemoveExercise,
  onMoveExercise,
}) {
  const theme = resolveTheme(accentColor);
  const [editingBlock, setEditingBlock] = useState(false);

  const notInBlock = exercises.filter(
    e => !blockExercises.find(pe => pe.exercise_id === e.id)
  );
  const categories = [...new Set(notInBlock.map(e => e.category).filter(Boolean))].sort();
  const availableExercises = notInBlock.filter(e => {
    const matchSearch = !exSearch || e.title.toLowerCase().includes(exSearch.toLowerCase());
    const matchCategory = !exCategory || e.category === exCategory;
    return matchSearch && matchCategory;
  });

  return (
    <div>
      {/* Mörkt huvud (T-13) */}
      <div className="fr-darkhead" style={{ position: 'relative', overflow: 'hidden', background: NEUTRAL.ink, borderRadius: theme.card_radius, padding: '22px 24px', marginBottom: '16px', color: '#FFFFFF' }}>
        <div aria-hidden="true" style={{ position: 'absolute', top: '-150px', right: '-70px', width: '360px', height: '360px', borderRadius: '999px', background: theme.hero_glow, opacity: 0.30, filter: 'blur(40px)', pointerEvents: 'none' }} />
        <div className="fr-darkhead-inner" style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ minWidth: 0 }}>
            <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.75)', cursor: 'pointer', padding: 0, fontSize: '12px', fontFamily: 'inherit' }}>← {selectedProgram?.title || 'Program'}</button>
            <h2 style={{ margin: '6px 0 0', fontSize: '22px', fontWeight: '800', color: '#FFFFFF', fontFamily: 'var(--font-heading)', lineHeight: 1.1 }}>Fas {(blockIndex >= 0 ? blockIndex : 0) + 1} · {selectedBlock.title}</h2>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', marginTop: '3px' }}>Vecka {selectedBlock.start_week}–{selectedBlock.end_week} · {blockExercises.length} {blockExercises.length === 1 ? 'övning' : 'övningar'} · set &amp; reps</div>
          </div>
          {!editingBlock && (
            <button type="button" onClick={() => setEditingBlock(true)} style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.22)', color: '#FFFFFF', padding: '9px 14px', borderRadius: theme.btn_radius, fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Redigera fas</button>
          )}
        </div>
      </div>

      {editingBlock && (
        <EditableBlock
          block={selectedBlock}
          blockIndex={blockIndex >= 0 ? blockIndex : 0}
          theme={theme}
          onSave={onSaveBlock}
          onDelete={() => onBack()}
          editing={editingBlock}
          setEditing={setEditingBlock}
        />
      )}

      {/* ── Övningar i blocket ── */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px' }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '999px', background: theme.accent, flexShrink: 0 }} />
          <span style={{ fontSize: '11px', fontWeight: '600', color: theme.text_secondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Övningar i detta block · {blockExercises.length}
          </span>
        </div>

        {blockExercises.length === 0 ? (
          <div style={{
            border: `1.5px dashed ${theme.card_border}`, borderRadius: '10px',
            background: theme.page_bg, padding: '22px 12px', textAlign: 'center',
          }}>
            <div style={{ fontSize: '22px', marginBottom: '6px' }}>🤸</div>
            <div style={{ fontSize: '12px', color: theme.text_secondary, marginBottom: '10px' }}>Inga övningar i fasen ännu</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {blockExercises.map((pe, index) => {
              const isEditing = editingPeId === pe.id;
              return (
                <div key={pe.id} style={{
                  background: isEditing ? theme.accent + '08' : theme.page_bg,
                  border: `1px solid ${isEditing ? theme.accent : theme.card_border}`,
                  borderRadius: '10px', padding: '10px 12px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {/* Ordningsknappar */}
                    {!isEditing && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', flexShrink: 0 }}>
                        <button
                          onClick={() => onMoveExercise(index, -1)}
                          disabled={index === 0}
                          style={{ background: 'none', border: 'none', color: index === 0 ? theme.card_border : theme.text_secondary, cursor: index === 0 ? 'default' : 'pointer', padding: '1px 4px', lineHeight: 1, fontSize: '0.7em', borderRadius: '3px' }}
                        >▲</button>
                        <button
                          onClick={() => onMoveExercise(index, 1)}
                          disabled={index === blockExercises.length - 1}
                          style={{ background: 'none', border: 'none', color: index === blockExercises.length - 1 ? theme.card_border : theme.text_secondary, cursor: index === blockExercises.length - 1 ? 'default' : 'pointer', padding: '1px 4px', lineHeight: 1, fontSize: '0.7em', borderRadius: '3px' }}
                        >▼</button>
                      </div>
                    )}
                    {/* Ikon-ruta */}
                    <div style={{ width: '32px', height: '32px', flexShrink: 0, background: theme.accent_soft_bg, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                      💪
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '500', color: theme.text_primary }}>{pe.exercise?.title}</div>
                      {!isEditing && doseText(pe) && (
                        <div style={{ fontSize: '11px', color: theme.text_secondary, marginTop: '1px' }}>{doseText(pe)}</div>
                      )}
                      {!isEditing && pe.notes && (
                        <div style={{ fontSize: '11px', color: theme.text_secondary, fontStyle: 'italic', marginTop: '2px' }}>{pe.notes}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      {!isEditing && (
                        <>
                          <button onClick={() => onSetEditingPeId(pe.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: theme.text_secondary, padding: '0 2px' }}
                            onMouseEnter={e => e.currentTarget.style.color = theme.accent}
                            onMouseLeave={e => e.currentTarget.style.color = theme.text_secondary}
                          >Set & reps</button>
                          <span style={{ color: theme.card_border }}>|</span>
                        </>
                      )}
                      <button
                        onClick={() => onRemoveExercise(pe.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: theme.text_secondary, padding: '0 2px', lineHeight: 1 }}
                        onMouseEnter={e => e.currentTarget.style.color = STATUS_COLORS.error}
                        onMouseLeave={e => e.currentTarget.style.color = theme.text_secondary}
                      >×</button>
                    </div>
                  </div>
                  {isEditing && (
                    <DoseForm
                      initial={pe}
                      saveLabel="Spara set & reps"
                      onSave={(dose) => onUpdateExerciseDose(pe.id, dose)}
                      onCancel={() => onSetEditingPeId(null)}
                      theme={theme}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Lägg till övning (övningsbibliotek) ── */}
      {exercises.length === 0 ? (
        <div style={{ padding: '14px 18px', background: STATUS_COLORS.warning_bg, borderRadius: '12px', border: `1px solid ${STATUS_COLORS.warning}40`, color: STATUS_COLORS.warning, fontSize: '0.875em' }}>
          Du har inga övningar i biblioteket. Gå till fliken "Övningar" för att skapa övningar.
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '999px', background: theme.accent, flexShrink: 0 }} />
            <span style={{ fontSize: '11px', fontWeight: '600', color: theme.text_secondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Lägg till övning
            </span>
          </div>

          <Card theme={theme} style={{ padding: '14px' }}>
            {pendingExercise ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  <div style={{ width: '32px', height: '32px', flexShrink: 0, background: theme.accent_soft_bg, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>💪</div>
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '13px', color: theme.text_primary }}>{pendingExercise.title}</div>
                    {pendingExercise.category && (
                      <Badge theme={theme} variant="accent">{pendingExercise.category}</Badge>
                    )}
                  </div>
                </div>
                <DoseForm
                  initial={null}
                  saveLabel="Lägg till övning"
                  onSave={(dose) => onAddExercise(pendingExercise.id, dose)}
                  onCancel={() => onSetPendingExercise(null)}
                  theme={theme}
                />
              </div>
            ) : (
              <>
                {notInBlock.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <FormInput
                      theme={theme}
                      type="text"
                      placeholder="Sök övning..."
                      value={exSearch}
                      onChange={e => onSetExSearch(e.target.value)}
                      style={{ flex: '1 1 160px', marginBottom: 0, minWidth: '120px' }}
                    />
                    {categories.length > 1 && (
                      <select
                        value={exCategory}
                        onChange={e => onSetExCategory(e.target.value)}
                        style={{ padding: '8px 10px', borderRadius: '8px', border: `1px solid ${theme.card_border}`, fontSize: '0.875em', outline: 'none', color: exCategory ? theme.text_primary : theme.text_secondary, background: theme.card_bg }}
                      >
                        <option value="">Alla kategorier</option>
                        {categories.map(c => <option key={c} value={c}>{c}  </option>)}
                      </select>
                    )}
                  </div>
                )}

                {notInBlock.length === 0 ? (
                  <div style={{ padding: '12px 16px', background: STATUS_COLORS.success_bg, borderRadius: '10px', border: `1px solid ${STATUS_COLORS.success}40`, color: STATUS_COLORS.success, fontSize: '0.875em' }}>
                    Alla övningar är redan tillagda i detta block.
                  </div>
                ) : availableExercises.length === 0 ? (
                  <EmptyState theme={theme} message="Inga övningar matchar sökningen." />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {availableExercises.map(e => (
                      <ListItem
                        key={e.id}
                        theme={theme}
                        onClick={() => { onSetPendingExercise(e); onSetExSearch(''); onSetExCategory(''); }}
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: '500', fontSize: '0.875em' }}>{e.title}</span>
                            {e.category && <Badge theme={theme} variant="accent">{e.category}</Badge>}
                          </div>
                        }
                        meta={e.description}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

export default ProgramBlockDetail;
